import { useMemo, useState, useRef, useCallback } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Trees } from 'lucide-react'

interface TreeVisualizationProps {
  totalTrees: number
  userTrees: number
  projectName: string
}

interface TreePosition {
  id: number
  x: number
  y: number
  isUser: boolean
}

export const TreeVisualization = ({ totalTrees, userTrees, projectName }: TreeVisualizationProps) => {
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 })
  const [isDragging, setIsDragging] = useState(false)
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 })
  const containerRef = useRef<HTMLDivElement>(null)

  const treePositions = useMemo(() => {
    const positions: TreePosition[] = []
    
    // Create array of tree indices and shuffle to randomize user trees
    const treeIndices = Array.from({ length: totalTrees }, (_, i) => i)
    const shuffled = [...treeIndices].sort(() => Math.random() - 0.5)
    const userTreeIndices = new Set(shuffled.slice(0, userTrees))

    // Create centered horizontal arrangement like in reference image
    for (let i = 0; i < totalTrees; i++) {
      // Arrange trees in a centered horizontal line with slight clustering
      const treeSpacing = Math.min(60 / totalTrees, 8) // Limit spacing for many trees
      const startX = 50 - (totalTrees - 1) * treeSpacing / 2 // Center the group
      const baseX = startX + i * treeSpacing
      
      // Add slight vertical and horizontal randomness while keeping centered
      const x = Math.max(20, Math.min(80, baseX + (Math.random() - 0.5) * 4))
      const y = Math.max(45, Math.min(55, 50 + (Math.random() - 0.5) * 6)) // Keep vertically centered
      
      positions.push({
        id: i,
        x,
        y,
        isUser: userTreeIndices.has(i)
      })
    }

    return positions
  }, [totalTrees, userTrees])

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    setIsDragging(true)
    setDragStart({ x: e.clientX - panOffset.x, y: e.clientY - panOffset.y })
  }, [panOffset])

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDragging) return
    setPanOffset({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y
    })
  }, [isDragging, dragStart])

  const handleMouseUp = useCallback(() => {
    setIsDragging(false)
  }, [])

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0]
    setIsDragging(true)
    setDragStart({ x: touch.clientX - panOffset.x, y: touch.clientY - panOffset.y })
  }, [panOffset])

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isDragging) return
    const touch = e.touches[0]
    setPanOffset({
      x: touch.clientX - dragStart.x,
      y: touch.clientY - dragStart.y
    })
  }, [isDragging, dragStart])

  const handleTouchEnd = useCallback(() => {
    setIsDragging(false)
  }, [])

  return (
    <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
      <CardHeader className="text-center">
        <CardTitle className="flex items-center justify-center gap-2 text-green-800">
          <Trees className="h-6 w-6" />
          {projectName}
        </CardTitle>
        <CardDescription className="text-green-700">
          Gesamt: {totalTrees} Bäume • Durch Ihre Unterstützung: {userTrees} Bäume
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div 
          ref={containerRef}
          className={`relative bg-green-25 rounded-lg p-4 min-h-[350px] overflow-hidden ${isDragging ? 'cursor-grabbing' : 'cursor-grab'} touch-pan-x touch-pan-y`}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          style={{ touchAction: 'none' }}
        >
          {/* Background gradient for ground effect */}
          <div className="absolute inset-0 bg-gradient-to-b from-green-25 via-green-50 to-green-100 rounded-lg"></div>
          
          {/* Trees */}
          <div 
            className="relative h-full"
            style={{
              transform: `translate(${panOffset.x}px, ${panOffset.y}px)`,
              transition: isDragging ? 'none' : 'transform 0.2s ease-out'
            }}
          >
            {treePositions.map((tree) => (
              <div
                key={tree.id}
                className="absolute transform -translate-x-1/2 transition-all duration-500 hover:scale-110"
                style={{
                  left: `${tree.x}%`,
                  top: `${tree.y}%`,
                  transform: `translateX(-50%) translateY(-50%)`,
                }}
              >
                {/* Tree SVG */}
                <div className={`w-12 h-16 ${tree.isUser ? 'filter drop-shadow-lg' : ''}`}>
                  <svg
                    viewBox="0 0 32 40"
                    className="w-full h-full"
                    fill="none"
                  >
                    {/* Tree trunk */}
                    <rect
                      x="14"
                      y="28"
                      width="4"
                      height="8"
                      fill="#8B4513"
                      rx="1"
                    />
                    
                    {/* Tree crown */}
                    <circle
                      cx="16"
                      cy="20"
                      r="12"
                      fill={tree.isUser ? "#FFD700" : "#22C55E"}
                      className={tree.isUser ? "animate-pulse" : ""}
                    />
                    
                    {/* Inner tree details */}
                    <circle
                      cx="16"
                      cy="20"
                      r="8"
                      fill={tree.isUser ? "#FFC107" : "#16A34A"}
                      opacity="0.7"
                    />
                    
                    {/* Smaller inner circle */}
                    <circle
                      cx="16"
                      cy="18"
                      r="5"
                      fill={tree.isUser ? "#FF9800" : "#15803D"}
                      opacity="0.5"
                    />
                  </svg>
                </div>
                
                {/* Tooltip for user trees */}
                {tree.isUser && (
                  <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-yellow-100 text-yellow-800 text-xs px-2 py-1 rounded-full border border-yellow-300 opacity-0 hover:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none">
                    Ihr Baum #{tree.id + 1}
                  </div>
                )}
              </div>
            ))}
          </div>
          
          {/* Legend */}
          <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm rounded-lg p-3 shadow-sm border">
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm">
                <div className="w-4 h-4 rounded-full bg-gradient-to-br from-yellow-400 to-yellow-600"></div>
                <span className="text-gray-700 font-medium">Ihre Bäume ({userTrees})</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <div className="w-4 h-4 rounded-full bg-gradient-to-br from-green-400 to-green-600"></div>
                <span className="text-gray-600">Andere Bäume ({totalTrees - userTrees})</span>
              </div>
            </div>
          </div>
        </div>
        
        {userTrees > 0 && (
          <div className="mt-4 text-center">
            <p className="text-sm text-green-700 font-medium">
              🌱 Herzlichen Dank für Ihren Beitrag zum Umweltschutz! 
            </p>
            <p className="text-xs text-green-600 mt-1">
              Jede Ihrer Empfehlungen hilft dabei, unsere Welt grüner zu machen.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  )
}